<template>
  <!-- MODAL DE GESTIÓN DE EMPRESAS -->
  <div v-if="isOpen" class="fixed inset-0 z-70 overflow-hidden">
    <!-- Overlay de fondo -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>

    <!-- Container del modal -->
    <div class="fixed inset-0 flex items-center justify-center p-0">
      <div class="relative bg-white w-full h-full flex flex-col">

        <!-- HEADER DEL MODAL COMPACTO -->
        <div class="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 sticky top-0 z-10">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
              </div>
              <div>
                <h2 class="text-lg font-bold">Gestión de Empresas</h2>
                <p class="text-emerald-100 text-xs">Administre las empresas de efibox</p>
              </div>
            </div>
            <button @click="closeModal" class="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- CONTENIDO PRINCIPAL -->
        <div class="flex-1 flex flex-col overflow-hidden">

          <!-- VISTA DE TABLA (Por defecto) -->
          <div v-show="!showForm" class="flex flex-col h-full">

            <!-- BARRA SUPERIOR COMPACTA - VERSIÓN MEJORADA -->
            <div class="bg-gray-50 border-b border-gray-200 p-4 flex-shrink-0">
              <div class="grid grid-cols-1 lg:grid-cols-4 gap-3">

                <!-- Búsqueda y Contador -->
                <div class="lg:col-span-2 space-y-3">
                  <!-- Búsqueda -->
                  <div class="relative">
                    <input v-model="searchText" type="text" placeholder="Buscar empresas..." class="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors">
                    <svg class="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <button v-if="searchText" @click="searchText = ''" class="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>

                  <!-- Contador de resultados con botón limpiar integrado -->
                  <div class="flex items-end">
                    <div :class="[
                      'text-sm text-gray-600 whitespace-nowrap',
                      hasActiveFilters ? 'w-full text-left' : 'text-left'
                    ]">
                      Se muestran {{ empresasFiltradas.length }} resultados<span v-if="hasActiveFilters">, presione en
                        <button @click="clearAllFilters" class="text-red-600 underline hover:text-red-800 font-medium transition-colors">
                          Limpiar
                        </button>
                        para quitar los filtros</span>.
                    </div>
                  </div>
                </div>

                <!-- Filtro por Región -->
                <div>
                  <div class="relative">
                    <button @click="toggleDropdown('region')" class="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-left flex items-center justify-between">
                      <span class="text-gray-700 truncate">{{ selectedRegion || 'Todas' }}</span>
                      <svg :class="['w-4 h-4 text-gray-400 transition-transform flex-shrink-0', dropdownOpen.region ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </button>

                    <div v-if="dropdownOpen.region" class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <button @click="selectOption('region', '')" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 first:rounded-t-lg">
                        Todas
                      </button>
                      <button v-for="region in regionesUnicas" :key="region" @click="selectOption('region', region)" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 truncate" :title="region">
                        {{ region }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Filtro por Tipo y Botones -->
                <div class="flex flex-col gap-3">
                  <!-- Filtro por Tipo -->
                  <div>
                    <div class="relative">
                      <button @click="toggleDropdown('tipo')" class="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-left flex items-center justify-between">
                        <span class="text-gray-700 truncate">{{ selectedTipo ? getTipoEmpresaNombre(selectedTipo) : 'Todos' }}</span>
                        <svg :class="['w-4 h-4 text-gray-400 transition-transform', dropdownOpen.tipo ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </button>

                      <div v-if="dropdownOpen.tipo" class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <button @click="selectOption('tipo', '')" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 first:rounded-t-lg">
                          Todos
                        </button>
                        <button v-for="tipo in tiposEmpresaUnicos" :key="tipo.id" @click="selectOption('tipo', tipo.id)" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50">
                          {{ tipo.nombre_tipo }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Botones: Actualizar y Añadir -->
                  <div class="flex items-center justify-end gap-2 mt-2">
                    <!-- Botón Actualizar - Solo ícono -->
                    <button @click="loadEmpresas" :disabled="loadingEmpresas" class="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50" title="Actualizar lista">
                      <svg :class="['w-4 h-4', loadingEmpresas ? 'animate-spin' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                    </button>

                    <!-- Botón Añadir - Con estilo específico -->
                    <button v-if="puedeCrearEmpresa" @click="showCreateForm" class="bg-white text-center text-sm w-36 rounded-xl h-10 relative text-black font-medium group border border-emerald-600 transition-all duration-300 overflow-hidden">
                      <div class="flex items-center justify-center h-full relative z-10 transition-all duration-300 group-hover:text-white">
                        <svg class="w-4 h-4 mr-1.5 transition-all duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                        </svg>
                        <span>Añadir Empresa</span>
                      </div>
                      <div class="absolute inset-0 bg-emerald-600 rounded-xl transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out"></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contenedor scrolleable para la tabla -->
            <div class="flex-1 overflow-y-auto p-6">
              <!-- Tabla de empresas -->
              <div v-if="empresasFiltradas.length > 0" class="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="empresa in empresasFiltradas" :key="empresa.id" class="hover:bg-gray-50 transition-colors">
                        <!-- Información de la empresa -->
                        <td class="px-6 py-4">
                          <div class="flex items-center">
                            <div class="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                              <img v-if="empresa.media" :src="empresa.media" class="w-full h-full object-cover" alt="Logo">
                              <svg class="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                              </svg>
                            </div>
                            <div>
                              <div class="text-sm font-medium text-gray-900">{{ empresa.nombre_empresa }}</div>
                              <div class="text-sm text-gray-500">RUT: {{ empresa.rut_empresa || 'No especificado' }}</div>
                            </div>
                          </div>
                        </td>

                        <!-- Información de contacto -->
                        <td class="px-6 py-4">
                          <div class="text-sm text-gray-900">{{ empresa.contacto_principal || 'Sin contacto' }}</div>
                          <div class="text-sm text-gray-500">{{ empresa.email_empresa || 'Sin email' }}</div>
                          <div class="text-sm text-gray-500">{{ empresa.telefono_empresa || 'Sin teléfono' }}</div>
                        </td>

                        <!-- Ubicación -->
                        <td class="px-6 py-4">
                          <div class="text-sm text-gray-900">{{ empresa.ciudad_empresa || 'Sin ciudad' }}</div>
                          <div class="text-sm text-gray-500">{{ empresa.region_empresa || 'Sin región' }}</div>
                        </td>

                        <!-- Tipo -->
                        <td class="px-6 py-4">
                          <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {{ getTipoEmpresaNombre(empresa.tipo_empesa) }}
                          </span>
                        </td>

                        <!-- Acciones -->
                        <td class="px-6 py-4 text-center">
                          <div class="flex items-center justify-center space-x-2">
                            <!-- Botón Editar -->
                            <button v-if="puedeEditarEmpresa" @click="editEmpresa(empresa)" :disabled="isLoading" class="p-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50" title="Editar empresa">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                              </svg>
                            </button>

                            <!-- Botón Eliminar -->
                            <button v-if="puedeEliminarEmpresa" @click="showDeleteConfirmation(empresa)" :disabled="isLoading" class="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Eliminar empresa">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Mensaje sin resultados de filtros -->
              <div v-else-if="empresas.length > 0 && empresasFiltradas.length === 0" class="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>
                <h3 class="text-lg font-medium text-gray-900 mb-2">No se encontraron empresas</h3>
                <p class="text-gray-600 mb-4">No hay empresas que coincidan con los filtros aplicados</p>
                <button @click="clearAllFilters" class="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors">
                  Limpiar Filtros
                </button>
              </div>

              <!-- Estado vacío -->
              <div v-else class="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <div class="flex flex-col items-center">
                  <svg class="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  <p class="text-lg font-medium mb-2">No hay empresas registradas</p>
                  <p class="text-sm mb-4">Comienza agregando la primera empresa a tu sistema</p>
                  <button v-if="puedeCrearEmpresa" @click="showCreateForm" class="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors">
                    Crear primera empresa
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- VISTA DE FORMULARIO (Toggle) -->
          <div v-show="showForm" class="p-6 overflow-y-auto">

            <!-- Header del formulario con botón volver -->
            <div class="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
              <div>
                <h3 class="text-xl font-semibold text-gray-900">
                  {{ isEditing ? 'Editar Empresa' : 'Nueva Empresa' }}
                </h3>
                <p class="text-sm text-gray-500 mt-1">
                  {{ isEditing ? 'Modifica la información de la empresa' : 'Completa todos los campos para registrar la empresa' }}
                </p>
              </div>
              <button @click="cancelForm" class="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                <span>Volver a la lista</span>
              </button>
            </div>

            <!-- Formulario -->
            <form @submit.prevent="submitForm" class="grid grid-cols-1 lg:grid-cols-2 gap-6">

              <!-- Columna izquierda -->
              <div class="space-y-4">

                <!-- Nombre de la empresa -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Nombre de la Empresa *
                  </label>
                  <input v-model="formData.nombre_empresa" type="text" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50" placeholder="eFintel S.A.">
                </div>

                <!-- RUT de la empresa -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    RUT de la Empresa *
                  </label>
                  <input @input="formatRut" v-model="formData.rut_empresa" type="text" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50" placeholder="12.345.678-9">
                </div>

                <!-- Email corporativo -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Email Corporativo *
                  </label>
                  <input v-model="formData.email_empresa" type="email" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50" placeholder="contacto@efintel.cl">
                </div>

                <!-- Teléfono -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono *
                  </label>
                  <input @input="formatTelefono" v-model="formData.telefono_empresa" type="tel" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50" placeholder="+56 9 1234 5678">
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Logo de la Empresa
                  </label>

                  <div class="flex items-center space-x-4">
                    <div class="shrink-0">
                      <div v-if="imagePreview || formData.media" class="relative">
                        <img :src="imagePreview || formData.media" class="h-16 w-16 object-cover rounded-full border border-gray-200" alt="Logo preview">
                        <button v-if="imagePreview" type="button" @click="clearImage" class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 shadow-sm hover:bg-red-600">
                          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div v-else class="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200 text-gray-400">
                        <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>

                    <div class="flex-1">
                      <input type="file" ref="fileInput" accept="image/*" @change="handleFileUpload" class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 transition-colors" />
                      <p class="text-xs text-gray-500 mt-1">PNG, JPG, GIF hasta 5MB</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Columna derecha -->
              <div class="space-y-4">

                <!-- Contacto principal -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Contacto Principal *
                  </label>
                  <input v-model="formData.contacto_principal" type="text" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50" placeholder="Juan Pérez - Gerente General">
                </div>

                <!-- Región -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    Región *
                  </label>
                  <select v-model="formData.region_empresa" required :disabled="isLoading" class="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50 bg-white">
                    <option value="">-- Seleccionar Región --</option>
                    <option v-for="region in regiones" :key="region" :value="region">{{ region }}</option>
                  </select>
                </div>

                <!-- Ciudad (dependiente de región) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    Ciudad *
                  </label>
                  <select v-model="formData.ciudad_empresa" required :disabled="isLoading || !formData.region_empresa" class="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50 bg-white">
                    <option value="">
                      {{ formData.region_empresa ? '-- Seleccionar Ciudad --' : '-- Primero seleccione región --' }}
                    </option>
                    <option v-for="ciudad in ciudadesDisponibles" :key="ciudad" :value="ciudad">
                      {{ ciudad }}
                    </option>
                  </select>
                  <p v-if="!formData.region_empresa" class="text-xs text-gray-500 mt-1">
                    Selecciona una región primero
                  </p>
                </div>

                <!-- Tipo de empresa -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    Tipo de Empresa *
                  </label>
                  <select v-model="formData.tipo_empresa" required :disabled="isLoading || loadingTiposEmpresa" class="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors disabled:opacity-50 bg-white">
                    <option value="">{{ loadingTiposEmpresa ? 'Cargando tipos...' : '-- Seleccionar Tipo --' }}</option>
                    <option v-for="tipo in tiposEmpresa" :key="tipo.id" :value="tipo.id">
                      {{ tipo.nombre_tipo }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Botones del formulario -->
              <div class="lg:col-span-2 flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                <button type="button" @click="cancelForm" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
                  Cancelar
                </button>
                <button type="submit" :disabled="isLoading || !isFormValid" class="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
                  <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{{ isLoading ? 'Procesando...' : (isEditing ? 'Actualizar Empresa' : 'Crear Empresa') }}</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- MODAL DE CONFIRMACIÓN DE ELIMINACIÓN -->
  <div v-if="showDeleteModal" class="fixed inset-0 z-50" style="z-index: 9999;">
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

    <div class="fixed inset-0 flex items-center justify-center p-4">
      <div class="relative bg-white rounded-lg w-full max-w-md">

        <!-- Header del modal -->
        <div class="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 rounded-t-lg">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold">Confirmar Eliminación</h3>
              <p class="text-red-100 text-sm">Esta acción no se puede deshacer</p>
            </div>
          </div>
        </div>

        <!-- Contenido del modal -->
        <div class="p-6">
          <div class="text-center">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>

            <h4 class="text-lg font-semibold text-gray-900 mb-2">
              ¿Eliminar empresa?
            </h4>

            <div v-if="empresaToDelete" class="bg-gray-50 rounded-lg p-4 mb-4">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                </div>
                <div class="text-left">
                  <div class="font-medium text-gray-900">
                    {{ empresaToDelete.nombre_empresa }}
                  </div>
                  <div class="text-sm text-gray-500">RUT: {{ empresaToDelete.rut_empresa }}</div>
                  <div class="text-sm text-gray-500">{{ empresaToDelete.email_empresa }}</div>
                </div>
              </div>
            </div>

            <p class="text-gray-600 mb-6">
              Esta acción eliminará permanentemente la empresa y toda su información asociada.
              También se eliminarán todos los usuarios y dispositivos relacionados.
              <strong class="text-red-600">No podrás recuperar estos datos posteriormente.</strong>
            </p>
          </div>

          <!-- Botones del modal -->
          <div class="flex items-center justify-end space-x-3">
            <button @click="cancelDelete" :disabled="isDeleting" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button @click="confirmDelete" :disabled="isDeleting" class="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
              <svg v-if="isDeleting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ isDeleting ? 'Eliminando...' : 'Sí, Eliminar' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/auth/auth.js'
import PermissionsService from '@/services/auth/permissions.js'
import { toast } from 'vue-sonner'

export default {
  name: 'ManageCompany',

  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close', 'empresa-changed'],

  data() {
    return {
      // Estados de la UI
      isLoading: false,
      showForm: false,
      isEditing: false,
      editingEmpresaId: null,

      // Estados para eliminación
      showDeleteModal: false,
      empresaToDelete: null,
      isDeleting: false,

      // Estados de filtros
      searchText: '',
      selectedRegion: '',
      selectedTipo: '',

      // Estados de dropdown
      dropdownOpen: {
        region: false,
        tipo: false
      },
      selectedFile: null,
      imagePreview: null,

      // Datos del formulario
      formData: {
        nombre_empresa: '',
        rut_empresa: '',
        email_empresa: '',
        telefono_empresa: '',
        ciudad_empresa: '',
        region_empresa: '',
        contacto_principal: '',
        tipo_empresa: '',
        media: null
      },

      // Datos de la aplicación
      empresas: [],
      tiposEmpresa: [],

      // Estados de carga
      loadingEmpresas: false,
      loadingTiposEmpresa: false,

      previousPhoneLength: 0,

      // Regiones de Chile
      regiones: [
        'Región de Arica y Parinacota',
        'Región de Tarapacá',
        'Región de Antofagasta',
        'Región de Atacama',
        'Región de Coquimbo',
        'Región de Valparaíso',
        'Región Metropolitana de Santiago',
        'Región del Libertador General Bernardo O\'Higgins',
        'Región del Maule',
        'Región de Ñuble',
        'Región del Biobío',
        'Región de La Araucanía',
        'Región de Los Ríos',
        'Región de Los Lagos',
        'Región Aysén del General Carlos Ibáñez del Campo',
        'Región de Magallanes y de la Antártica Chilena'
      ],

      // Mapa de ciudades por región
      ciudadesPorRegion: {
        'Región de Arica y Parinacota': ['Arica', 'Putre', 'General Lagos', 'Camarones'],
        'Región de Tarapacá': ['Iquique', 'Alto Hospicio', 'Pozo Almonte', 'Pica', 'Huara', 'Camiña', 'Colchane'],
        'Región de Antofagasta': ['Antofagasta', 'Calama', 'Tocopilla', 'Mejillones', 'Taltal', 'San Pedro de Atacama', 'María Elena', 'Ollagüe', 'Sierra Gorda'],
        'Región de Atacama': ['Copiapó', 'Vallenar', 'Caldera', 'Chañaral', 'Diego de Almagro', 'Tierra Amarilla', 'Huasco', 'Alto del Carmen', 'Freirina'],
        'Región de Coquimbo': ['La Serena', 'Coquimbo', 'Ovalle', 'Illapel', 'Vicuña', 'Andacollo', 'Monte Patria', 'Combarbalá', 'Los Vilos', 'Salamanca', 'La Higuera', 'Paihuano'],
        'Región de Valparaíso': ['Valparaíso', 'Viña del Mar', 'Quilpué', 'Villa Alemana', 'San Antonio', 'Quillota', 'Los Andes', 'San Felipe', 'Limache', 'La Calera', 'Casablanca', 'Concón', 'Quintero', 'Puchuncaví', 'Cartagena', 'El Quisco', 'Algarrobo', 'Santo Domingo', 'Isla de Pascua'],
        'Región Metropolitana de Santiago': ['Santiago', 'Puente Alto', 'Maipú', 'La Florida', 'Las Condes', 'Peñalolén', 'Ñuñoa', 'Providencia', 'San Bernardo', 'Pudahuel', 'La Pintana', 'El Bosque', 'Renca', 'Quilicura', 'Lo Prado', 'Estación Central', 'Cerrillos', 'Conchalí', 'Huechuraba', 'Independencia', 'La Reina', 'Macul', 'Vitacura', 'San Miguel', 'La Cisterna', 'Lo Espejo', 'Pedro Aguirre Cerda', 'San Joaquín', 'San Ramón', 'Recoleta', 'Quinta Normal', 'Lo Barnechea', 'Colina', 'Lampa', 'Tiltil', 'Pirque', 'San José de Maipo', 'Buin', 'Paine', 'Melipilla', 'Talagante', 'Peñaflor', 'Padre Hurtado', 'Curacaví', 'El Monte', 'Isla de Maipo', 'María Pinto', 'San Pedro', 'Alhué'],
        'Región del Libertador General Bernardo O\'Higgins': ['Rancagua', 'San Fernando', 'Rengo', 'Machalí', 'Graneros', 'San Vicente', 'Pichilemu', 'Santa Cruz', 'Chimbarongo', 'Nancagua', 'Placilla', 'Peumo', 'Coinco', 'Coltauco', 'Doñihue', 'Las Cabras', 'Requínoa', 'Mostazal', 'Codegua', 'Olivar', 'Quinta de Tilcoco'],
        'Región del Maule': ['Talca', 'Curicó', 'Linares', 'Constitución', 'Molina', 'Cauquenes', 'Parral', 'San Javier', 'San Clemente', 'Maule', 'Pelarco', 'Río Claro', 'Teno', 'Romeral', 'Rauco', 'Licantén', 'Vichuquén', 'Hualañé', 'Chanco', 'Pelluhue'],
        'Región de Ñuble': ['Chillán', 'Chillán Viejo', 'Bulnes', 'San Carlos', 'Quirihue', 'Yungay', 'Coihueco', 'El Carmen', 'Pemuco', 'Pinto', 'San Ignacio', 'Ñiquén', 'Portezuelo', 'Quillón', 'Ranquil', 'Treguaco', 'Cobquecura', 'Ninhue', 'San Nicolás', 'Coelemu'],
        'Región del Biobío': ['Concepción', 'Talcahuano', 'Los Ángeles', 'Chiguayante', 'San Pedro de la Paz', 'Coronel', 'Lota', 'Tomé', 'Hualpén', 'Penco', 'Lebu', 'Arauco', 'Cañete', 'Curanilahue', 'Cabrero', 'Nacimiento', 'Mulchén', 'Santa Juana', 'Laja', 'Santa Bárbara', 'Tucapel', 'Negrete', 'Yumbel', 'Hualqui', 'Florida', 'Contulmo', 'Tirúa', 'Los Álamos'],
        'Región de La Araucanía': ['Temuco', 'Padre Las Casas', 'Villarrica', 'Pucón', 'Angol', 'Lautaro', 'Nueva Imperial', 'Victoria', 'Collipulli', 'Carahue', 'Traiguén', 'Pitrufquén', 'Loncoche', 'Gorbea', 'Cunco', 'Freire', 'Curacautín', 'Lonquimay', 'Perquenco', 'Galvarino', 'Purén', 'Los Sauces', 'Lumaco', 'Ercilla', 'Renaico', 'Cholchol', 'Toltén', 'Teodoro Schmidt', 'Saavedra', 'Curarrehue', 'Melipeuco'],
        'Región de Los Ríos': ['Valdivia', 'La Unión', 'Río Bueno', 'Panguipulli', 'Paillaco', 'Los Lagos', 'Futrono', 'Lanco', 'Máfil', 'Mariquina', 'Corral', 'Lago Ranco'],
        'Región de Los Lagos': ['Puerto Montt', 'Osorno', 'Castro', 'Ancud', 'Puerto Varas', 'Quellón', 'Calbuco', 'Maullín', 'Frutillar', 'Llanquihue', 'Los Muermos', 'Fresia', 'Chonchi', 'Dalcahue', 'Puqueldón', 'Quemchi', 'Quinchao', 'Curaco de Vélez', 'Chaitén', 'Futaleufú', 'Hualaihué', 'Palena', 'Puerto Octay', 'Purranque', 'Río Negro', 'San Juan de la Costa', 'San Pablo'],
        'Región Aysén del General Carlos Ibáñez del Campo': ['Coyhaique', 'Puerto Aysén', 'Chile Chico', 'Cochrane', 'Puerto Cisnes', 'Río Ibáñez', 'Lago Verde', 'Guaitecas', 'O\'Higgins', 'Tortel'],
        'Región de Magallanes y de la Antártica Chilena': ['Punta Arenas', 'Puerto Natales', 'Puerto Williams', 'Porvenir', 'Primavera', 'Timaukel', 'Cabo de Hornos', 'Antártica', 'Laguna Blanca', 'Río Verde', 'San Gregorio', 'Torres del Paine']
      }
    }
  },

  computed: {
    puedeCrearEmpresa() {
      this.permisosVersion
      return PermissionsService.can('empresas', 'crear')
    },
    puedeEditarEmpresa() {
      this.permisosVersion
      return PermissionsService.can('empresas', 'editar')
    },
    puedeEliminarEmpresa() {
      this.permisosVersion
      return PermissionsService.can('empresas', 'eliminar')
    },

    isFormValid() {
      return this.formData.nombre_empresa &&
        this.formData.rut_empresa &&
        this.formData.email_empresa &&
        this.formData.telefono_empresa &&
        this.formData.ciudad_empresa &&
        this.formData.region_empresa &&
        this.formData.contacto_principal &&
        this.formData.tipo_empresa
    },

    ciudadesDisponibles() {
      if (!this.formData.region_empresa) return []
      return this.ciudadesPorRegion[this.formData.region_empresa] || []
    },

    empresasFiltradas() {
      let result = [...this.empresas];

      if (this.searchText) {
        const search = this.searchText.toLowerCase();
        result = result.filter(empresa =>
          (empresa.nombre_empresa && empresa.nombre_empresa.toLowerCase().includes(search)) ||
          (empresa.rut_empresa && empresa.rut_empresa.toLowerCase().includes(search)) ||
          (empresa.contacto_principal && empresa.contacto_principal.toLowerCase().includes(search)) ||
          (empresa.email_empresa && empresa.email_empresa.toLowerCase().includes(search)) ||
          (empresa.telefono_empresa && empresa.telefono_empresa.toLowerCase().includes(search)) ||
          (empresa.ciudad_empresa && empresa.ciudad_empresa.toLowerCase().includes(search)) ||
          (empresa.region_empresa && empresa.region_empresa.toLowerCase().includes(search)) ||
          (this.getTipoEmpresaNombre(empresa.tipo_empesa).toLowerCase().includes(search))
        );
      }

      if (this.selectedRegion) {
        result = result.filter(empresa => empresa.region_empresa === this.selectedRegion);
      }

      if (this.selectedTipo) {
        result = result.filter(empresa => empresa.tipo_empesa === this.selectedTipo);
      }

      return result;
    },

    hasActiveFilters() {
      return !!(this.searchText || this.selectedRegion || this.selectedTipo);
    },

    regionesUnicas() {
      const regiones = new Set();
      this.empresas.forEach(empresa => {
        if (empresa.region_empresa) regiones.add(empresa.region_empresa);
      });
      return Array.from(regiones).sort();
    },

    tiposEmpresaUnicos() {
      const tiposUsados = new Set();
      this.empresas.forEach(empresa => {
        if (empresa.tipo_empesa) tiposUsados.add(empresa.tipo_empesa);
      });

      return this.tiposEmpresa.filter(tipo => tiposUsados.has(tipo.id));
    }
  },

  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.showError('La imagen no debe superar los 5MB');
        event.target.value = '';
        return;
      }

      this.selectedFile = file;
      this.imagePreview = URL.createObjectURL(file);
    },

    clearImage() {
      this.selectedFile = null;
      this.imagePreview = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    toggleDropdown(tipo) {
      Object.keys(this.dropdownOpen).forEach(key => {
        if (key !== tipo) this.dropdownOpen[key] = false;
      });
      this.dropdownOpen[tipo] = !this.dropdownOpen[tipo];
    },

    selectOption(tipo, valor) {
      if (tipo === 'region') this.selectedRegion = valor;
      else if (tipo === 'tipo') this.selectedTipo = valor;
      this.dropdownOpen[tipo] = false;
    },

    closeAllDropdowns() {
      Object.keys(this.dropdownOpen).forEach(key => {
        this.dropdownOpen[key] = false;
      });
    },

    clearAllFilters() {
      this.searchText = '';
      this.selectedRegion = '';
      this.selectedTipo = '';
      this.closeAllDropdowns();
    },

    handleClickOutside(event) {
      if (!event.target.closest('.relative')) this.closeAllDropdowns();
    },

    closeModal() {
      document.body.style.overflow = ''
      this.resetAll();
      this.$emit('close');
    },

    resetAll() {
      this.showForm = false;
      this.showDeleteModal = false;
      this.empresaToDelete = null;
      this.resetForm();
      this.clearAllFilters();
    },

    resetForm() {
      this.formData = {
        nombre_empresa: '',
        rut_empresa: '',
        email_empresa: '',
        telefono_empresa: '',
        ciudad_empresa: '',
        region_empresa: '',
        contacto_principal: '',
        tipo_empresa: ''
      };
      this.isEditing = false;
      this.editingEmpresaId = null;
    },

    showCreateForm() {
      this.resetForm();
      this.showForm = true;
    },

    cancelForm() {
      this.resetForm();
      this.showForm = false;
    },

    editEmpresa(empresa) {
      console.log('✏️ Preparando edición de empresa:', empresa);

      try {
        this.isEditing = true;
        this.editingEmpresaId = empresa.id;

        this.selectedFile = null;
        this.imagePreview = null;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';

        this.formData = {
          nombre_empresa: empresa.nombre_empresa || '',
          rut_empresa: empresa.rut_empresa || '',
          email_empresa: empresa.email_empresa || '',
          telefono_empresa: empresa.telefono_empresa || '',
          ciudad_empresa: empresa.ciudad_empresa || '',
          region_empresa: empresa.region_empresa || '',
          contacto_principal: empresa.contacto_principal || '',
          tipo_empresa: empresa.tipo_empesa || '',
          media: empresa.media || null
        };

        this.showForm = true;

      } catch (error) {
        console.error('❌ Error preparando edición:', error);
        this.showError('Error al cargar datos de la empresa para edición');
      }
    },

    async updateEmpresa() {
      this.isLoading = true;

      try {
        console.log('🔄 ACTUALIZANDO EMPRESA ID:', this.editingEmpresaId);

        if (!this.editingEmpresaId) throw new Error('ID de empresa no válido para edición');
        if (!this.formData.tipo_empresa) throw new Error('Debe seleccionar un tipo de empresa');

        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        const formData = new FormData();
        formData.append('nombre_empresa', this.formData.nombre_empresa.trim());
        formData.append('rut_empresa', this.formData.rut_empresa.trim());
        formData.append('email_empresa', this.formData.email_empresa.trim());
        formData.append('telefono_empresa', this.formData.telefono_empresa.trim());
        formData.append('ciudad_empresa', this.formData.ciudad_empresa.trim());
        formData.append('region_empresa', this.formData.region_empresa);
        formData.append('contacto_principal', this.formData.contacto_principal.trim());
        formData.append('tipo_empesa', parseInt(this.formData.tipo_empresa));

        if (this.selectedFile) formData.append('media', this.selectedFile);

        this.baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';
        const updateUrl = `${this.baseURL}/empresas/api/empresa/${this.editingEmpresaId}/`;

        const response = await fetch(updateUrl, {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'X-CSRFToken': csrfToken,
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          },
          body: formData
        });

        const responseText = await response.text();

        if (!response.ok) {
          let errorData;
          try {
            errorData = JSON.parse(responseText);
          } catch {
            throw new Error(`Error del servidor (${response.status}): ${responseText}`);
          }

          let mensajeError = 'Error al actualizar empresa:\n';

          if (errorData.nombre_empresa) {
            const nombreErrors = Array.isArray(errorData.nombre_empresa) ? errorData.nombre_empresa : [errorData.nombre_empresa];
            mensajeError += `• Nombre: ${nombreErrors.join(', ')}\n`;
          }

          if (errorData.rut_empresa) {
            const rutErrors = Array.isArray(errorData.rut_empresa) ? errorData.rut_empresa : [errorData.rut_empresa];
            mensajeError += `• RUT: ${rutErrors.join(', ')}\n`;
          }

          if (errorData.email_empresa) {
            const emailErrors = Array.isArray(errorData.email_empresa) ? errorData.email_empresa : [errorData.email_empresa];
            mensajeError += `• Email: ${emailErrors.join(', ')}\n`;
          }

          if (errorData.tipo_empesa) {
            const tipoErrors = Array.isArray(errorData.tipo_empesa) ? errorData.tipo_empesa : [errorData.tipo_empesa];
            mensajeError += `• Tipo de empresa: ${tipoErrors.join(', ')}\n`;
          }

          if (errorData.non_field_errors) {
            const generalErrors = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors : [errorData.non_field_errors];
            mensajeError += `• ${generalErrors.join(', ')}\n`;
          }

          throw new Error(mensajeError.trim() || `Error del servidor (${response.status})`);
        }

        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch {
          throw new Error('Respuesta del servidor no válida');
        }

        const empresaIndex = this.empresas.findIndex(e => e.id === this.editingEmpresaId);
        if (empresaIndex !== -1) this.empresas[empresaIndex] = responseData;

        const mensajeExito =
          `Empresa ${responseData.nombre_empresa} actualizada exitosamente.`

        this.showSuccess(mensajeExito);

        this.resetForm();

        if (typeof this.clearImage === 'function') this.clearImage();
        else {
          this.selectedFile = null;
          this.imagePreview = null;
        }

        this.showForm = false;

      } catch (error) {
        console.error('❌ Error actualizando empresa:', error);
        this.showError(error.message);
      } finally {
        this.isLoading = false;
      }
    },

    showDeleteConfirmation(empresa) {
      console.log('🗑️ Solicitando eliminación de empresa:', empresa);
      this.empresaToDelete = empresa;
      this.showDeleteModal = true;
    },

    cancelDelete() {
      console.log('❌ Eliminación cancelada');
      this.showDeleteModal = false;
      this.empresaToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.empresaToDelete) {
        this.showError('No hay empresa seleccionada para eliminar');
        return;
      }

      this.isDeleting = true;

      try {
        console.log('🗑️ ELIMINANDO EMPRESA ID:', this.empresaToDelete.id);

        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        this.baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';
        const deleteUrl = `${this.baseURL}/empresas/api/empresa/${this.empresaToDelete.id}/`;

        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          }
        });

        if (!response.ok) {
          const responseText = await response.text();
          let errorMessage = `Error del servidor (${response.status})`;

          try {
            const errorData = JSON.parse(responseText);
            if (errorData.detail) errorMessage = errorData.detail;
            else if (errorData.message) errorMessage = errorData.message;
          } catch (e) { throw new Error(errorMessage); }
        }

        const empresaIndex = this.empresas.findIndex(e => e.id === this.empresaToDelete.id);
        if (empresaIndex !== -1) this.empresas.splice(empresaIndex, 1);

        const mensajeExito =
          `Empresa ${this.empresaToDelete.nombre_empresa} eliminada exitosamente.`

        this.showSuccess(mensajeExito);
        this.$emit('empresa-changed');

        this.showDeleteModal = false;
        this.empresaToDelete = null;

      } catch (error) {
        console.error('❌ Error eliminando empresa:', error);
        this.showError(`Error al eliminar empresa: ${error.message}`);
      } finally {
        this.isDeleting = false;
      }
    },

    async submitForm() {
      if (!this.isFormValid) {
        this.showError('Por favor completa todos los campos requeridos');
        return;
      }

      this.isLoading = true;

      try {
        if (this.isEditing) {
          await this.updateEmpresa();
        } else {
          await this.createEmpresa();
          this.$emit('empresa-changed');
        }
      } catch (error) {
        console.error('Error en el formulario:', error);
        this.showError(`Error: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },

    async createEmpresa() {
      try {
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        const formData = new FormData();
        formData.append('nombre_empresa', this.formData.nombre_empresa);
        formData.append('rut_empresa', this.formData.rut_empresa);
        formData.append('email_empresa', this.formData.email_empresa);
        formData.append('telefono_empresa', this.formData.telefono_empresa);
        formData.append('ciudad_empresa', this.formData.ciudad_empresa);
        formData.append('region_empresa', this.formData.region_empresa);
        formData.append('contacto_principal', this.formData.contacto_principal);
        formData.append('tipo_empesa', parseInt(this.formData.tipo_empresa));

        if (this.selectedFile) formData.append('media', this.selectedFile);

        console.log('🆕 CREANDO EMPRESA (FormData preparado)');
        this.baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';

        const response = await fetch(`${this.baseURL}/empresas/api/empresa/`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRFToken': csrfToken,
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Error al crear empresa');
        }

        const responseData = await response.json();
        console.log('✅ Empresa creada exitosamente:', responseData);

        this.empresas.push(responseData);

        this.resetForm();

        if (typeof this.clearImage === 'function') this.clearImage();
        else {
          this.selectedFile = null;
          this.imagePreview = null;
        }

        this.showForm = false;

        this.showSuccess(`Empresa ${responseData.nombre_empresa} creada exitosamente.`);

      } catch (error) {
        console.error('❌ Error creando empresa:', error);
        // submitForm() ya muestra el error con showError, así evitamos duplicar.
        throw error;
      }
    },

    formatRut(event) {
      let value = event.target.value;
      let cleanValue = value.replace(/[^0-9kK]/gi, '');

      if (cleanValue.length > 9) cleanValue = cleanValue.substring(0, 9);

      let body = cleanValue.slice(0, -1);
      let dv = cleanValue.slice(-1);

      if (/[kK]/.test(body)) body = body.replace(/[kK]/gi, '');

      const formattedBody = body.length > 0 ? new Intl.NumberFormat('de-DE').format(body) : body;

      let finalRut = formattedBody;
      if (cleanValue.length > body.length) finalRut += `-${dv.toUpperCase()}`;

      this.formData.rut_empresa = finalRut;
    },

    formatTelefono() {
      let input = this.formData.telefono_empresa;
      let digits = input.replace(/\D/g, '');

      if (!input.startsWith('+') && digits.length > 0) digits = '+' + digits;
      else if (input.startsWith('+')) digits = '+' + digits;

      if (digits.length > 16) digits = digits.substring(0, 16);

      let formatted = '';
      if (digits.startsWith('+56')) {
        const code = digits.substring(0, 3);
        const number = digits.substring(3);

        if (number.length > 0) {
          if (number.startsWith('9') && number.length > 1) {
            formatted = `${code} ${number.charAt(0)} ${number.substring(1, 5)} ${number.substring(5, 9)}`;
          } else {
            formatted = `${code} ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5, 9)}`;
          }
        } else {
          formatted = code;
        }
      } else {
        formatted = digits;
      }

      this.formData.telefono_empresa = formatted.trim();
    },

    async loadEmpresas() {
      if (this.loadingEmpresas) return;
      this.loadingEmpresas = true;
      try {
        console.log('🔄 CARGANDO EMPRESAS desde API');
        const response = await AuthService.authenticatedRequest('/empresas/api/empresa/');
        this.empresas = response.results || [];
        console.log('✅ Empresas cargadas:', this.empresas.length);
      } catch (error) {
        console.error('❌ Error cargando empresas:', error);
        this.empresas = [];
      } finally {
        this.loadingEmpresas = false;
      }
    },

    async loadTiposEmpresa() {
      if (this.loadingTiposEmpresa) return;
      this.loadingTiposEmpresa = true;

      try {
        console.log('🏢 CARGANDO TIPOS DE EMPRESA desde API');
        const tipos = await AuthService.authenticatedRequest('/empresas/api/tipos_empresa/');

        if (Array.isArray(tipos)) {
          this.tiposEmpresa = tipos;
          console.log('✅ Tipos de empresa cargados:', this.tiposEmpresa.length);
        } else {
          throw new Error('Respuesta no es un array válido');
        }

      } catch (error) {
        console.error('❌ Error cargando tipos de empresa:', error);
        this.tiposEmpresa = [
          { id: 1, nombre_tipo: "Tecnología" },
          { id: 2, nombre_tipo: "Manufactura" },
          { id: 3, nombre_tipo: "Servicios" },
          { id: 4, nombre_tipo: "Educación" },
          { id: 5, nombre_tipo: "Salud" },
          { id: 6, nombre_tipo: "Construcción" },
          { id: 7, nombre_tipo: "Retail" },
          { id: 8, nombre_tipo: "Agricultura" },
          { id: 9, nombre_tipo: "Minería" },
          { id: 10, nombre_tipo: "Energía" },
          { id: 11, nombre_tipo: "Transporte" },
          { id: 12, nombre_tipo: "Telecomunicaciones" },
          { id: 13, nombre_tipo: "Financiera" },
          { id: 14, nombre_tipo: "Consultoría" },
          { id: 15, nombre_tipo: "Otro" }
        ];
      } finally {
        this.loadingTiposEmpresa = false;
      }
    },

    getTipoEmpresaNombre(tipoId) {
      if (!tipoId) return 'Sin tipo';
      const tipo = this.tiposEmpresa.find(t => t.id === tipoId);
      return tipo ? tipo.nombre_tipo : `Tipo ${tipoId}`;
    },

    // ✅ Reemplazo final: sin alerts, usando vue-sonner
    showSuccess(message) {
      toast.success(message, { duration: 3500 });
    },

    showError(message) {
      toast.error(message, { duration: 5000 });
    },

    showInfo(message) {
      toast.info(message, { duration: 3500 });
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.body.style.overflow = ''
    document.removeEventListener('click', this.handleClickOutside);
    this.resetAll();
  },

  watch: {
    'formData.region_empresa': {
      handler(nuevaRegion, regionAnterior) {
        if (regionAnterior && nuevaRegion !== regionAnterior) {
          console.log('🗺️ Región cambió, reseteando ciudad');
          this.formData.ciudad_empresa = '';
        }
      },
      immediate: false
    },

    'formData.rut_empresa': { handler() { } },
    'formData.telefono_empresa': { handler() { } },

    isOpen: {
      async handler(newValue) {
        document.body.style.overflow = newValue ? 'hidden' : ''

        if (newValue && this.empresas.length === 0) {
          console.log('🔍 Modal abierto - cargando datos iniciales...');

          try {
            await Promise.all([
              this.loadEmpresas(),
              this.loadTiposEmpresa()
            ]);

            console.log('✅ Datos iniciales cargados');
          } catch (error) {
            console.error('❌ Error cargando datos iniciales:', error);
          }
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
/* Transiciones suaves */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animaciones para formularios */
.form-enter-active,
.form-leave-active {
  transition: all 0.3s ease;
}

.form-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.form-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Animaciones para dropdowns */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>